/**
 * CreateFolderDialog Component
 *
 * shadcn Dialog for creating a new folder.
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { FolderSelect } from './FolderSelect'
import type { EmailFolder, EmailFolderCreate } from '@/lib/email-templates/folder-types'

interface CreateFolderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folders: EmailFolder[]
  defaultParentId?: number | null
  onSubmit: (data: EmailFolderCreate) => Promise<void>
  isSubmitting: boolean
}

export function CreateFolderDialog({
  open,
  onOpenChange,
  folders,
  defaultParentId,
  onSubmit,
  isSubmitting,
}: CreateFolderDialogProps) {
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState<number | null>(defaultParentId ?? null)

  useEffect(() => {
    if (open) {
      setName('')
      setParentId(defaultParentId ?? null)
    }
  }, [open, defaultParentId])

  const handleSubmit = async () => {
    if (!name.trim()) return
    await onSubmit({
      name: name.trim(),
      parentId,
      sortOrder: 0,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
          <DialogDescription>
            Create a folder to organize templates.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q2 2026"
              className="rounded-lg"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Parent Folder</Label>
            <FolderSelect
              folders={folders}
              value={parentId}
              onChange={setParentId}
              placeholder="(Root level)"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Folder'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
